import React, { useRef } from 'react'
import classnames from 'classnames'
import Checkbox from 'assets/dropdown/checkbox.svg'
import CheckboxSelected from 'assets/dropdown/checkbox-selected.svg'

import Caret from 'components/Caret/Caret'
import { PanelOption } from 'frontend/types'
import { Direction } from 'frontend/enums'

import styles from './Panel.module.css'

interface PanelProps {
  activeLabels?: string[]
  ariaLabel?: string
  dataTrk?: string
  handlePanelClick: () => void
  isOpen: boolean
  label: string
  onOptionSelect: (option: PanelOption) => void
  options: PanelOption[]
}

const PanelItem = ({ isSelected, option, onOptionSelect, isOpen, dataTrk }) => {
  let ariaLabel = isSelected ? `${option.label}, vald` : ''
  ariaLabel = !option.available ? `${option.label}, ej valbar` : ariaLabel

  return (
    <li>
      <button
        aria-label={ariaLabel}
        aria-disabled={!option.available}
        className={classnames(styles.optionButton, {
          [`${styles.isChecked}`]: isSelected,
        })}
        disabled={!option.available && !isSelected}
        data-testid={`panel-option-${option.label}`}
        data-trk={dataTrk}
        onClick={() => onOptionSelect(option)}
        tabIndex={isOpen ? 0 : -1}
        type="button"
      >
        {isSelected ? (
          <CheckboxSelected aria-hidden focusable="false" />
        ) : (
          <Checkbox aria-hidden focusable="false" />
        )}
        <div className={classnames(styles.option)}>
          <div>{option.label}</div>
        </div>
      </button>
    </li>
  )
}

const Panel = ({
  activeLabels,
  ariaLabel,
  dataTrk,
  handlePanelClick,
  isOpen,
  label,
  onOptionSelect,
  options,
}: PanelProps) => {
  const caretDirection = isOpen ? Direction.Up : Direction.Down
  const optionsContainerRef = useRef(null)

  return (
    <div className={styles.panelContainer}>
      <button
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={classnames(styles.button, {
          [`${styles.hasSetFilter}`]: activeLabels && activeLabels.length > 0,
        })}
        data-testid="toggle-button"
        data-trk={`${dataTrk}-toggle`}
        onClick={() => handlePanelClick()}
        type="button"
      >
        <span>{label}</span>
        <Caret animate direction={caretDirection} styling={styles.caret} />
      </button>
      <ul
        aria-hidden={!isOpen}
        ref={optionsContainerRef}
        className={classnames(styles.optionsContainer, {
          [`${styles.expanded}`]: isOpen,
          [`${styles.collapsed}`]: !isOpen,
        })}
      >
        {options.map((option, index) => {
          const isSelected = activeLabels?.includes(option.label)

          return (
            <PanelItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              isSelected={isSelected}
              option={option}
              onOptionSelect={onOptionSelect}
              isOpen={isOpen}
              dataTrk={dataTrk}
            />
          )
        })}
      </ul>
    </div>
  )
}

export default Panel
